
import {
  computed,
  defineComponent, onMounted, Ref, ref, ComputedRef, onUnmounted, watch,
} from 'vue';
import AppHeader from '@/components/AppHeader.vue';
import AppSearch from '@/components/UI/AppSearch.vue';
import { useDriveStore } from '@/modules/drive/store/driveStore';
import { ISpacesContact } from '@/types/interfaces';
import SpacesService from '@/modules/spaces/services/SpacesService';
import AppContactItem from '@/components/AppContactItem.vue';
import AppLoader from '@/components/UI/AppLoader.vue';

import { useMessagesStore } from '../store/messagesStore';
import {
  ACCOUNT_STATUS, CONTACT_STATUS, GROUP_USER_STATUS, NETWORK_CONTACT_TYPES,
} from '@/types/enums';
import { useMainStore } from '@/store/mainStore';
import { useSpacesStore } from '@/modules/spaces/store/spacesStore';
import AppSkeletonContact from '@/components/skeletons/AppSkeletonContact.vue';
import AppItemWrapper from '@/components/AppItemWrapper.vue';

export default defineComponent({
  emits: ['close'],
  components: {
    AppHeader,
    AppSearch,
    AppContactItem,
    AppLoader,
    AppSkeletonContact,
    AppItemWrapper,
  },
  setup(props, { emit }) {
    const driveStore = useDriveStore();
    const messagesStore = useMessagesStore();
    const mainStore = useMainStore();
    const spacesStore = useSpacesStore();
    const contacts: Ref<ISpacesContact[]> = ref([]);
    const groupOwner: Ref<ISpacesContact> = ref({} as ISpacesContact);

    const dataLoading: Ref<boolean> = ref(false);
    const isAddingMembers: Ref<boolean> = ref(false);

    const filteredContacts: ComputedRef<ISpacesContact[]> = computed(
      () => contacts.value.filter((cont) => {
        const filteredSearch = `${cont.firstName} ${cont.lastName}`.toLowerCase().includes(driveStore.search.toLowerCase());
        return filteredSearch && !cont.defaultUser;
      }),
    );

    /** Computes an array of invited user account IDs for the current group */
    const invitedUserIds = computed(() => {
      const currentGroup = spacesStore.myGroups.find((group) => group.id === messagesStore.currentConversation?.group?.id);
      if (currentGroup?.invitedUsers?.length) {
        return currentGroup.invitedUsers.map((user) => user.accountId);
      }
      return [];
    });

    function getGroupMember(contact: ISpacesContact): ISpacesContact | undefined {
      return messagesStore.currentConversation?.group?.members.find((member) => member.accountId === contact.accountId);
    }

    function setSelectedStatus() {
      contacts.value = contacts.value.map((cont) => ({
        ...cont,
        selected: !!getGroupMember(cont),
      }));
    }

    function resolveUserType(contactStatus: CONTACT_STATUS, accountId: string): NETWORK_CONTACT_TYPES {
      if (contactStatus === CONTACT_STATUS.REQUEST_SENT) {
        return NETWORK_CONTACT_TYPES.OUTCOMING;
      }
      if (contactStatus === CONTACT_STATUS.REQUEST_RECEIVED) {
        return NETWORK_CONTACT_TYPES.INVITATION;
      }
      if (contactStatus === CONTACT_STATUS.CONTACT) {
        return NETWORK_CONTACT_TYPES.MY;
      }
      if (accountId === mainStore.user.accountId) {
        return NETWORK_CONTACT_TYPES.ME;
      }
      return NETWORK_CONTACT_TYPES.SUGGESTION;
    }

    function inviteToGroup(contact: ISpacesContact) {
      const groupId = messagesStore.currentConversation?.group?.id;
      if (groupId) {
        const payload = [{
          initiatorId: mainStore.user.accountId,
          targetId: contact.accountId,
          groupId,
        }];

        SpacesService.inviteUsersToGroup(payload);
        const currentGroup = spacesStore.myGroups.find((group) => group.id === groupId);
        if (currentGroup) {
          currentGroup.invitedUsers.push(contact);
        }
      }
    }

    const myContactsSearch = computed(() => {
      const arr = [...spacesStore.searchContactsList.filter((cont) => {
        const type = resolveUserType(cont.contactStatus, cont.accountId);
        return type === NETWORK_CONTACT_TYPES.MY && !cont.defaultUser;
      })];
      return arr;
    });

    const notMyContactsSearch = computed(() => {
      const arr = [...spacesStore.searchContactsList.filter((cont) => {
        const type = resolveUserType(cont.contactStatus, cont.accountId);
        return type !== NETWORK_CONTACT_TYPES.MY && type !== NETWORK_CONTACT_TYPES.ME && !cont.defaultUser;
      })];
      return arr;
    });

    // search logic
    const scroller = ref<HTMLElement | null>(null);

    let pagesAmount = 0;
    let currentPageIndex = 0;

    function searchContacts(query: string, pageIndex: number, pageSize: number, push?: boolean): void {
      spacesStore.searchContacts(query, pageIndex, pageSize, push).then((response) => {
        if (response.success) {
          pagesAmount = response.results.data.totalPages;
          currentPageIndex = response.results.data.number;
        }
      });
    }

    function onScroll() {
      const isBottom = Math.round(scroller.value!.scrollTop + scroller.value!.clientHeight) >= Math.round(scroller.value!.scrollHeight);

      if (isBottom && pagesAmount !== currentPageIndex + 1) {
        searchContacts(spacesStore.search, currentPageIndex + 1, 10, true);
      }
    }

    let timeout: ReturnType<typeof setTimeout>;
    watch(() => driveStore.search, (newSearch, oldSearch) => {
      clearTimeout(timeout);
      if (newSearch === '') {
        spacesStore.searchContactsList = [];
      } else {
        timeout = setTimeout(() => {
          searchContacts(newSearch, 0, 10);
        }, 300);
      }
    });

    onMounted(() => {
      dataLoading.value = true;
      SpacesService.fetchMyContacts()
        .then((response) => {
          if (response.success) {
            contacts.value = response.results.data.map((cont) => cont.user).filter((cont) => {
              if (cont.type !== GROUP_USER_STATUS.OWNER) {
                return true;
              }
              groupOwner.value = cont;
              return false;
            });
            setSelectedStatus();
          }
        })
        .finally(() => { dataLoading.value = false; });
    });

    onUnmounted(() => {
      driveStore.search = '';
    });

    return {
      driveStore,
      contacts,
      messagesStore,
      dataLoading,
      isAddingMembers,
      filteredContacts,
      myContactsSearch,
      notMyContactsSearch,
      onScroll,
      invitedUserIds,
      inviteToGroup,
      scroller,
    };
  },
});
